/**
 * Présence temps réel des boîtiers (battement MQTT ~5 s).
 *
 * Trois sources, par ordre de fraîcheur :
 *  1. push WebSocket sur TRANSITION (le serveur balaie toutes les 2 s ; un
 *     boîtier hors service n'émet rien, seul un timer serveur peut le
 *     détecter) ;
 *  2. GET /presence au montage, et en repli périodique ;
 *  3. vieillissement local : si plus rien n'arrive (WebSocket ET requêtes
 *     coupés), l'état passe à « indéterminé » au lieu de conserver un
 *     « en ligne » périmé.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiService } from '../services/ApiService';
import { webSocketService } from '../services/WebSocketService';

export type EtatPresence =
  | 'en_ligne'
  | 'retard'
  | 'hors_ligne'
  | 'indetermine'
  | 'inconnu';

export interface Presence {
  etat: EtatPresence;
  age_s?: number | null;
  motif?: string | null;
  derniere_vue?: string | null;
  device_id?: string;
}

export type CartePresence = Record<number, Presence>;

/**
 * LOT 7.4 — état de la SUPERVISION elle-même, distinct de l'état des boîtiers.
 *
 *   'ok'           : l'API a répondu ; ce que dit `presence` fait foi.
 *   'indisponible' : l'API n'a pas pu répondre (503 « état indéterminé »,
 *                    ou serveur injoignable depuis le navigateur).
 *
 * Ces deux cas produisaient exactement le même écran avant ce lot : une carte
 * de présence vide. « Aucun boîtier n'est en retard » et « l'état des
 * boîtiers est inconnu » doivent produire deux affichages distincts.
 */
export type EtatSupervision = 'ok' | 'indisponible';

/**
 * Les vues détail ne connaissent pas toutes la meme cle. /bain/:id porte
 * l'id du boitier (15), tandis que /rincage/:id, /niveau/:id et
 * /debit_pression/:id portent le suffixe du topic MQTT (2 pour rincage2).
 * D'ou la resolution par nom de device en plus de l'id.
 */
export const nomDevice = (prefixe: string, suffixe: string | number): string => {
  const s = String(suffixe ?? '');
  return s.startsWith(prefixe) ? s : `${prefixe}${s}`;
};

// Au-delà de ce délai, les données locales ne sont plus considérées comme valides.
const PEREMPTION_LOCALE_MS = 45000;
const REPLI_WS_OK_MS = 20000;
const REPLI_WS_COUPE_MS = 5000;

export function usePresence(): {
  presence: CartePresence;
  parDevice: Record<string, Presence>;
  presenceDe: (cle: number | string | undefined | null) => Presence | undefined;
  lienServeur: boolean;
  supervision: EtatSupervision;
} {
  const [presence, setPresence] = useState<CartePresence>({});
  const [lienServeur, setLienServeur] = useState<boolean>(true);
  const [supervision, setSupervision] = useState<EtatSupervision>('ok');
  const dernierRafraichissement = useRef<number>(0);

  useEffect(() => {
    let actif = true;

    const charger = async () => {
      try {
        const data = await apiService.getPresence();
        if (!actif) return;
        const carte: CartePresence = {};
        Object.entries(data.boitiers || {}).forEach(([id, p]: [string, any]) => {
          carte[Number(id)] = p as Presence;
        });
        setPresence(carte);
        setLienServeur(Boolean(data.lien_mqtt_serveur));
        // Une API antérieure au LOT 7 n'envoie pas `supervision` : elle a tout
        // de même répondu 200, donc 'ok'.
        setSupervision(data.supervision === 'indisponible' ? 'indisponible' : 'ok');
        dernierRafraichissement.current = Date.now();
      } catch {
        // 503 « état indéterminé », ou serveur injoignable. Dans les deux cas
        // l'état est inconnu. `supervision` bascule sur 'indisponible' au lieu
        // de conserver à l'écran une carte de présence périmée.
        if (!actif) return;
        setSupervision('indisponible');
      }
    };

    charger();

    const surPresence = (data: any) => {
      if (!actif || !data || typeof data.boitier_id !== 'number') return;
      setPresence((precedent) => ({
        ...precedent,
        [data.boitier_id]: {
          etat: data.etat,
          age_s: data.age_s,
          motif: data.motif,
          derniere_vue: data.derniere_vue,
          device_id: data.device_id,
        },
      }));
      // REMISE A 'ok' SUR PUSH WEBSOCKET.
      //
      // Sans elle, un seul 503 transitoire figeait la bannière « supervision
      // indisponible » définitivement : le repli périodique ne rappelle
      // `charger()` que si le WebSocket est coupé ou si plus rien n'est
      // arrivé depuis 20 s. Or quand les transitions arrivent à moins de 20 s
      // d'intervalle — le cas normal d'un parc actif — `charger()` n'est
      // jamais rappelé. Un push reçu prouve que le serveur répond.
      setSupervision('ok');
      dernierRafraichissement.current = Date.now();
    };
    const desabonner = webSocketService.on('presence', surPresence);

    // Repli : rapide si le WebSocket est coupé, filet de sécurité sinon.
    const timer = setInterval(() => {
      const wsOk = webSocketService.isConnectedToServer?.() ?? false;
      const age = Date.now() - dernierRafraichissement.current;
      if (!wsOk || age >= REPLI_WS_OK_MS) charger();
      if (age > PEREMPTION_LOCALE_MS) {
        setPresence((precedent) => {
          const perime: CartePresence = {};
          Object.entries(precedent).forEach(([id, p]) => {
            perime[Number(id)] = {
              ...p,
              etat: 'indetermine',
              motif: 'supervision injoignable depuis le navigateur',
            };
          });
          return perime;
        });
      }
    }, REPLI_WS_COUPE_MS);

    return () => {
      actif = false;
      clearInterval(timer);
      desabonner();
    };
  }, []);

  // Second index, derive du premier : une seule source de verite, donc le
  // vieillissement et les push WebSocket profitent aux deux acces.
  const parDevice = useMemo(() => {
    const carte: Record<string, Presence> = {};
    Object.values(presence).forEach((p) => {
      if (p.device_id) carte[p.device_id] = p;
    });
    return carte;
  }, [presence]);

  // Accepte indifferemment un id de boitier (15) ou un nom de device
  // ('rincage2') — les routes ne portent pas la meme cle selon le type.
  const presenceDe = useCallback(
    (cle: number | string | undefined | null): Presence | undefined => {
      if (cle === undefined || cle === null || cle === '') return undefined;
      if (typeof cle === 'number') return presence[cle];
      const n = Number(cle);
      if (Number.isFinite(n) && presence[n]) return presence[n];
      return parDevice[cle];
    },
    [presence, parDevice],
  );

  return { presence, parDevice, presenceDe, lienServeur, supervision };
}

export default usePresence;
